/**
 * Money + number presentation in the customer's language (OCP-656). Amounts
 * are stored in minor units (cents); the currency is whatever the row says
 * (EUR today). Uses the active portal locale for separators and symbol
 * placement (`1.234,56 €` in German, `€1,234.56` in English).
 */
import { intlTag } from '../i18n';

export function fmtMoney(cents: number | null | undefined, currency = 'EUR', fallback = '-'): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return fallback;
  return new Intl.NumberFormat(intlTag(), { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
}

export function fmtNumber(n: number | null | undefined, options: Intl.NumberFormatOptions = {}, fallback = '-'): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return fallback;
  return new Intl.NumberFormat(intlTag(), options).format(n);
}

/** `12.5 %` / `12,5 %` - one decimal by default. */
export function fmtPercent(value: number | null | undefined, digits = 1, fallback = '-'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  return `${new Intl.NumberFormat(intlTag(), { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(value)} %`;
}

/**
 * OCP-660 / AGB V4 § 3.1 - the customer-facing AI unit. `microcentsPerToken`
 * is the published rate (`ai.orboto_token_microcents`, default 10,000 = one
 * orboto Token per cent); the accounting stays in EUR microcents underneath.
 * "orboto Token" is identical in both languages.
 */
export function orbotoTokens(microcents: number, microcentsPerToken: number): number {
  if (!Number.isFinite(microcents) || !Number.isFinite(microcentsPerToken) || microcentsPerToken <= 0) return 0;
  return Math.round(microcents / microcentsPerToken);
}

export function fmtOrbotoTokens(microcents: number, microcentsPerToken: number, fallback = '-'): string {
  if (!Number.isFinite(microcents) || !Number.isFinite(microcentsPerToken) || microcentsPerToken <= 0) return fallback;
  return `${new Intl.NumberFormat(intlTag()).format(orbotoTokens(microcents, microcentsPerToken))} orboto Token`;
}
